import { useMutation } from '@apollo/client';
import Button from '@components/ui/button';
import Input from '@components/ui/input';
import TextArea from '@components/ui/text-area';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { ContactFormDocument } from './contact-form.gql';

interface ContactFormValues {
  name: string;
  email: string;
  telephone?: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactFormValues>({
    mode: 'onBlur',
  });

  function onSubmit(values: ContactFormValues) {
    contactFormMutation({
      variables: {
        input: {
          fullname: values.name,
          email: values.email,
          telephone: values.telephone ?? '',
          message: values.message,
        },
      },
    });
  }

  const [contactFormMutation, contactFormResult] = useMutation(ContactFormDocument);

  const { t } = useTranslation(['common']);

  if (contactFormResult.data) {
    return <>{t('contact-page-message-sent')}</>;
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mx-auto flex w-full flex-col justify-center "
      noValidate
    >
      <div className="flex flex-col space-y-5">
        <div className="flex flex-col space-y-5 md:flex-row md:space-y-0">
          <Input
            labelKey="forms:label-name-required"
            placeholderKey="forms:placeholder-name"
            {...register('name', {
              required: 'forms:name-required',
              pattern: {
                value: /[a-zA-Z]+/,
                message: 'forms:name-error',
              },
            })}
            className="w-full md:w-1/2 "
            errorKey={errors.name?.message}
            variant="solid"
          />
          <Input
            labelKey="forms:label-email-required"
            type="email"
            placeholderKey="forms:placeholder-email"
            {...register('email', {
              required: 'forms:email-required',
              validate: (value) => {
                if (value && !isEmail(value)) {
                  return 'forms:email-error';
                }
              },
            })}
            className="mt-2 w-full md:mt-0 md:w-1/2 md:ms-2.5 lg:ms-5"
            errorKey={errors.email?.message}
            variant="solid"
          />
        </div>
        <Input
          labelKey="forms:label-phone"
          {...register('telephone', {
            validate: (value) => {
              if (value && !isMobilePhone(value)) {
                return 'forms:phone-error';
              }
            },
          })}
          errorKey={errors.telephone?.message}
          className="relative"
          variant="solid"
        />
        <TextArea
          labelKey="forms:label-message-required"
          {...register('message', { required: 'forms:message-required' })}
          className="relative mb-4"
          placeholderKey="forms:placeholder-message"
          errorKey={errors.message?.message}
        />
        <div className="relative">
          <Button
            type="submit"
            className="mt-1 w-full text-sm sm:w-auto lg:text-base"
            loading={contactFormResult.loading}
          >
            {t('button-send-message')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
